import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  Chip,
  Divider,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  alpha,
  useTheme,
  Tooltip
} from '@mui/material';
import {
  Person as PersonIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  HourglassBottom as HourglassBottomIcon,
  AttachMoney as AttachMoneyIcon,
  Groups as GroupsIcon,
  Info as InfoIcon,
  CalendarToday as CalendarTodayIcon
} from '@mui/icons-material';
import { projectAPI, permitAPI } from '../../apis'; // Import permitAPI
import { Job, Permit } from '../../types';

interface ProjectLaborProps {
  projectId: string;
}

// interface JobWithPermitType extends Job {
//   permitType: string;
// }

const ProjectLabor: React.FC<ProjectLaborProps> = ({ projectId }) => {
  const theme = useTheme();
  // Store jobs grouped by permit type
  const [groupedJobs, setGroupedJobs] = useState<Record<string, Job[]>>({}); 
  const [permits, setPermits] = useState<Permit[]>([]);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analysisMessage, setAnalysisMessage] = useState<string | null>(null);

  const fetchProjectData = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    setAnalysisMessage(null);
    setGroupedJobs({}); // Clear previous data
    try {
      // 1. Fetch project details to get permits
      const projectResponse = await projectAPI.getProject(projectId); 
      if (projectResponse.status !== 'success' || !projectResponse.data) {
        throw new Error('Failed to fetch project details');
      }
      const fetchedPermits = projectResponse.data.permits || [];
      const permitMap = new Map(fetchedPermits.map(p => [p.permitID, p]));
      setPermits(fetchedPermits);

      if (fetchedPermits.length === 0) {
        setError("No permits found for this project. Add permits to analyze labor.");
        setLoading(false);
        return;
      }

      // 2. Fetch jobs for each permit
      const jobPromises = fetchedPermits.map(permit => 
        permitAPI.getPermitJobs(permit.permitID)
      );
      const jobResults = await Promise.allSettled(jobPromises);

      const jobsByPermitType: Record<string, Job[]> = {};
      let totalJobsFetched = 0;

      jobResults.forEach((result, index) => {
        const permitId = fetchedPermits[index].permitID;
        const permit = permitMap.get(permitId);
        const permitType = permit?.permitType || 'Unknown Permit Type';

        if (result.status === 'fulfilled' && result.value.status === 'success') {
          if (result.value.jobs.length > 0) {
             if (!jobsByPermitType[permitType]) {
               jobsByPermitType[permitType] = [];
             }
             jobsByPermitType[permitType].push(...result.value.jobs);
             totalJobsFetched += result.value.jobs.length;
          }
        } else {
          console.error(`Failed to fetch jobs for permit ${permitId}:`, result.status === 'rejected' ? result.reason : result.value.message);
        }
      });

      setGroupedJobs(jobsByPermitType);
      
      if (totalJobsFetched === 0 && fetchedPermits.length > 0) {
         setError("No labor data found. Click 'Analyze Labor' to generate.");
      }

    } catch (error) {
      console.error('Error fetching project labor data:', error);
      setError('Failed to fetch labor data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  const analyzeProjectLabor = async () => {
    try {
      setAnalyzing(true);
      setError(null);
      setAnalysisMessage(null);
      
      const response = await projectAPI.analyzeProjectJobs(projectId);
      
      if (response.status === 'success' || response.status === 'partial_success') {
        await fetchProjectData(); 
        setAnalysisMessage(response.message || 'Labor analysis complete.');
      } else {
        setError(response.message || 'Failed to analyze labor data.');
      }
    } catch (error) {
      console.error('Error analyzing labor:', error);
      setError('Failed to analyze labor data. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  useEffect(() => {
    fetchProjectData();
  }, [fetchProjectData]);

  // Calculate summaries from grouped data
  const getTotalEstimatedHours = () => {
    return Object.values(groupedJobs).flat().reduce((sum, job) => sum + (job.estimatedhours || 0), 0);
  };

  const getTotalEstimatedCost = () => {
    return Object.values(groupedJobs).flat().reduce((sum, job) => sum + ((job.estimatedhours || 0) * (job.avgcostperhour || 0)), 0);
  };

  const getAverageWorkers = () => {
    const allJobs = Object.values(groupedJobs).flat();
    if (allJobs.length === 0) return 0;
    const totalWorkers = allJobs.reduce((sum, job) => sum + (job.numworkers || 0), 0);
    return (totalWorkers / allJobs.length);
  };
  
  const hasJobs = Object.keys(groupedJobs).length > 0;

  return (
    <Grid container spacing={3} sx={{ p: 3 }}>
      {/* Labor Card */}
      <Grid item xs={12}>
        <Card sx={{ borderRadius: '16px', overflow: 'visible' }}>
          <CardHeader
            title="Project Labor Breakdown"
            action={
              <Button
                variant="contained"
                color="primary"
                startIcon={analyzing ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                onClick={analyzeProjectLabor}
                disabled={analyzing || loading || permits.length === 0} // Disable if no permits
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 500
                }}
              >
                {analyzing ? 'Analyzing...' : 'Analyze Labor'}
              </Button>
            }
            sx={{
              p: 3,
              pb: 0,
              '& .MuiCardHeader-title': {
                fontSize: '1.25rem',
                fontWeight: 600,
              },
            }}
          />
          <CardContent sx={{ p: 3, pt: 1 }}>
            {(error || analysisMessage) && (
              <Alert
                severity={error ? "info" : "success"}
                sx={{ mb: 2, borderRadius: '8px' }}
              >
                {error || analysisMessage}
              </Alert>
            )}

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : !hasJobs && !error ? (
               <Box sx={{ textAlign: 'center', p: 4 }}>
                 <Typography variant="body1" color="text.secondary">
                   {permits.length > 0 ? "No labor data found. Click 'Analyze Labor' above." : "No permits available for labor analysis."}
                 </Typography>
               </Box>
            ) : (
               // Iterate through grouped jobs
               Object.entries(groupedJobs).map(([permitType, jobsInGroup]) => (
                 <Box key={permitType} sx={{ mb: 4 }}>
                   <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                     {permitType}
                   </Typography>
                   <TableContainer component={Paper} elevation={0} variant="outlined" sx={{ borderRadius: '8px' }}>
                     <Table size="small">
                       <TableHead sx={{ bgcolor: alpha(theme.palette.grey[500], 0.04) }}>
                         <TableRow>
                           <TableCell sx={{ fontWeight: 600 }}>Job/Task Description</TableCell>
                           {/* Removed Permit Type Column */}
                           <TableCell align="right" sx={{ fontWeight: 600 }}>Est. Hours</TableCell>
                           <TableCell align="right" sx={{ fontWeight: 600 }}>Avg. Cost/Hr</TableCell>
                           <TableCell align="right" sx={{ fontWeight: 600 }}>Workers</TableCell>
                           <TableCell align="right" sx={{ fontWeight: 600 }}>Total Est. Cost</TableCell>
                         </TableRow>
                       </TableHead>
                       <TableBody>
                         {jobsInGroup.map((job) => (
                           <TableRow key={job.jobid} hover>
                             <TableCell>
                               <Typography variant="body2">{job.description}</Typography>
                             </TableCell>
                             {/* Removed Permit Type Cell */}
                             <TableCell align="right">{job.estimatedhours.toFixed(1)}</TableCell>
                             <TableCell align="right">{`$${job.avgcostperhour.toFixed(2)}`}</TableCell>
                             <TableCell align="right">{job.numworkers}</TableCell>
                             <TableCell align="right">
                               {`$${(job.estimatedhours * job.avgcostperhour).toFixed(2)}`}
                             </TableCell>
                           </TableRow>
                         ))}
                       </TableBody>
                     </Table>
                   </TableContainer>
                 </Box>
               ))
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Summary Cards - Only show if there are jobs */}
      {hasJobs && (
        <Grid item container xs={12} spacing={3} sx={{ mt: 0 }}>
          <Grid item xs={12} sm={4}>
            <Paper elevation={0} variant="outlined" sx={{ p: 2, textAlign: 'center', borderRadius: '12px' }}>
              <HourglassBottomIcon color="action" sx={{ mb: 1 }} />
              <Typography variant="h6">{getTotalEstimatedHours().toFixed(1)}</Typography>
              <Typography variant="body2" color="text.secondary">Total Est. Hours</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper elevation={0} variant="outlined" sx={{ p: 2, textAlign: 'center', borderRadius: '12px' }}>
              <AttachMoneyIcon color="action" sx={{ mb: 1 }} />
              <Typography variant="h6">${getTotalEstimatedCost().toFixed(2)}</Typography>
              <Typography variant="body2" color="text.secondary">Total Est. Cost</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper elevation={0} variant="outlined" sx={{ p: 2, textAlign: 'center', borderRadius: '12px' }}>
              <GroupsIcon color="action" sx={{ mb: 1 }} />
              <Typography variant="h6">{getAverageWorkers().toFixed(1)}</Typography>
              <Typography variant="body2" color="text.secondary">Avg. Workers/Task</Typography>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export default ProjectLabor; 